<script setup lang="ts">
import { computed, inject } from 'vue'
import { Info, AlertTriangle, XCircle, X } from 'lucide-vue-next'
import type { AlertStore } from './index'
const alertStore = inject<AlertStore>('alertStore')
const CONFIG = {
  info: { cls: 'alert-info', icon: Info },
  warn: { cls: 'alert-warning', icon: AlertTriangle },
  error: { cls: 'alert-error', icon: XCircle },
} as const
const alertConfig = computed(() => {
  const level = alertStore?.level || 'info'
  return CONFIG[level as keyof typeof CONFIG] || CONFIG.info
})
const updateShow = (v: boolean) => {
  if (alertStore) {
    alertStore.show = v
  }
}
</script>
<template>
  <Teleport to="body">
    <Transition name="toast">
      <div v-if="alertStore && alertStore.show" class="simple-alert-container">
        <div class="alert" :class="alertConfig.cls">
          <span class="alert-icon">
            <component :is="alertConfig.icon" :size="20" />
          </span>
          <div class="alert-body">
            <p class="alert-msg">{{ alertStore.message }}</p>
            <p v-if="alertStore.description" class="alert-desc">{{ alertStore.description }}</p>
          </div>
          <button v-if="alertStore.button" class="alert-close" @click="updateShow(false)" aria-label="닫기">
            <X :size="16" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
<style scoped>
.simple-alert-container {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  /* 프로젝트/데이터/세션 생성 모달 등(z-index 최대 10001)보다 항상 위에 떠야 하므로 여유있게 높은 값을 사용 */
  z-index: 20000;
  pointer-events: none;
  /* 컨테이너 자체는 클릭 무시 */
}

.alert {
  pointer-events: auto;
  /* 알림창은 클릭 가능 */
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-left: 4px solid;
  border-radius: 0 8px 8px 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  background: white;
  text-align: left;
  min-width: 420px;
  box-sizing: border-box;
}

/* 애니메이션 설정 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translate(-50%, -20px);
}

.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}

/* --- 이하 디자인 CSS는 이전과 동일 --- */
.alert-info {
  background: #eff6ff;
  border-color: #3b82f6;
}

.alert-info .alert-icon {
  color: #3b82f6;
}

.alert-info .alert-msg {
  color: #1e3a5f;
}

.alert-info .alert-close {
  color: #3b82f6;
}

.alert-warning {
  background: #fffbeb;
  border-color: #f59e0b;
}

.alert-warning .alert-icon {
  color: #f59e0b;
}

.alert-warning .alert-msg {
  color: #78350f;
}

.alert-warning .alert-close {
  color: #f59e0b;
}

.alert-error {
  background: #fff1f2;
  border-color: #f43f5e;
}

.alert-error .alert-icon {
  color: #f43f5e;
}

.alert-error .alert-msg {
  color: #4c0519;
}

.alert-error .alert-close {
  color: #f43f5e;
}

.alert-icon {
  flex-shrink: 0;
  display: flex;
}

.alert-body {
  flex: 1;
}

.alert-msg {
  font-size: 14px;
  font-weight: 600;
  white-space: pre-wrap;
  line-height: 1.5;
}

.alert-desc {
  font-size: 12px;
  margin-top: 4px;
}

.alert-close {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 2px;
  border-radius: 4px;
  flex-shrink: 0;
  opacity: 0.8;
  transition: opacity 0.15s;
}

.alert-close:hover {
  opacity: 1;
}
</style>